import { useContext, useReducer } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import ApiReducer from './ApiReducer';
import getContext from '../context/AppSettingsContext';
import { useLoaderStore } from '../context/LoaderContext';
import useApiLoaderHelper from '../utils/useApiLoaderHelper';
import useTriggerReleaseVersionCheck from 'src/utils/useTriggerReleaseVersionCheck';

let requestIdCounter = 0;

export default () => {
    const appSettings = useContext(getContext());
    const { releaseVersionCheck } = useTriggerReleaseVersionCheck();
    const { getLoaderDetailsType } = useApiLoaderHelper();
    const { dispatchLoaderAction } = useLoaderStore();
    const [state, dispatch] = useReducer(ApiReducer, { status: 'empty', isLoading: false, error: null, endpoint: null });

    type GetProps = {
        endpoint?: string | null;
        disableAppSettingsApiEndpoint?: boolean;
        queryParam?: string | null;
        additionalInfo?: any;
        callInBackground?: boolean;
        config?: AxiosRequestConfig | null;
        fullUrl?: string | null;
        forceRefreshCache?: boolean | null;
    };

    const getApiData = async (props: GetProps): Promise<any> => {
        const endpoint = props.endpoint;
        const additionalInfo = props.additionalInfo;
        const config = props.config;
        const forceRefreshCache = props.forceRefreshCache;
        let queryParam = props.queryParam ?? '';
        let fullUrl = props.fullUrl;
        let requestIdRef: string | undefined;

        try {
            if (!props.callInBackground) requestIdRef = handleLoaders('add', endpoint, additionalInfo, false);
            dispatch({ type: 'request_started', endpoint, additionalInfo });

            if (!endpoint && !fullUrl) {
                throw new Error('Missing url parameter');
            }
            const rootPath = props.disableAppSettingsApiEndpoint ? endpoint : `${appSettings.api}${endpoint}`;

            if (props.forceRefreshCache) {
                queryParam = queryParam?.length > 0 ? queryParam + '&forceRefreshCache=true' : '?forceRefreshCache=true';

                if (fullUrl) {
                    fullUrl += fullUrl.includes('?') ? '&forceRefreshCache=true' : '?forceRefreshCache=true';
                }
            }

            const url = fullUrl ?? rootPath + queryParam;
            const response = await axios.get(url, config ?? {});

            if (response.status !== 200) {
                throw new Error(`${response.status} ${response.statusText}`);
            }

            dispatch({ type: 'request_successful', result: response.data, endpoint, additionalInfo });
            if (!props.callInBackground) handleLoaders('remove', endpoint, additionalInfo, false, requestIdRef);
            releaseVersionCheck(response?.headers?.['release-version'], appSettings.releaseId);
            return response.data;
        } catch (error) {
            dispatch({ type: 'request_failed', error: error, endpoint, additionalInfo });
            if (!props.callInBackground) handleLoaders('remove', endpoint, additionalInfo, false, requestIdRef);
        }
    };

    type PostProps = {
        endpoint?: string | null;
        disableAppSettingsApiEndpoint?: boolean;
        returnFullResponseObject?: boolean;
        data?: any;
        queryParam?: string | null;
        config?: AxiosRequestConfig | null;
        additionalInfo?: any;
        callInBackground?: boolean;
        shouldReturnError?: boolean;
        skipRefreshCacheEndpoints?: boolean;
        fullUrl?: string | null;
    };
    const postApiData = async (props: PostProps): Promise<any> => {
        const endpoint = props.endpoint;
        const data = props.data;
        const config = props.config;
        const additionalInfo = props.additionalInfo;
        let queryParam = props.queryParam ?? '';
        let fullUrl = props.fullUrl;
        let requestIdRef: string | undefined;
        try {
            if (!props.callInBackground) requestIdRef = handleLoaders('add', endpoint, additionalInfo, true);
            dispatch({ type: 'request_started', endpoint, additionalInfo });

            if (!endpoint && !fullUrl) {
                throw new Error('Missing url parameter');
            }
            const rootPath = props.disableAppSettingsApiEndpoint ? endpoint : `${appSettings.api}${endpoint}`;

            if (props.skipRefreshCacheEndpoints) {
                queryParam = queryParam?.length > 0 ? queryParam + '&skipRefreshCache=true' : '?skipRefreshCache=true';

                if (fullUrl) {
                    fullUrl += fullUrl.includes('?') ? '&skipRefreshCache=true' : '?skipRefreshCache=true';
                }
            }
            const url = fullUrl ?? rootPath + queryParam;

            const response = await axios.post(url, data, config ?? {});

            if (response.status !== 200) {
                throw new Error(`${response.status} ${response.statusText}`);
            }

            dispatch({ type: 'request_successful', result: response.data, endpoint, additionalInfo });
            if (!props.callInBackground) handleLoaders('remove', endpoint, additionalInfo, true, requestIdRef);
            releaseVersionCheck(response?.headers?.['release-version'], appSettings.releaseId);
            return props.returnFullResponseObject ? response : response.data;
        } catch (error) {
            dispatch({ type: 'request_failed', error: error, endpoint, additionalInfo });
            if (!props.callInBackground) handleLoaders('remove', endpoint, additionalInfo, true, requestIdRef);
            if (props.shouldReturnError) {
                return error;
            }
        }
    };

    const handleLoaders = (type: 'add' | 'remove', endpoint: string, additionalInfo: any, isMutating: boolean, requestId?: string): string | undefined => {
        if (type === 'add') {
            const loaderDetail = getLoaderDetailsType(endpoint);
            if (loaderDetail) {
                if (additionalInfo?.hideLoader) return undefined;
                if (additionalInfo?.customLoaderMessage) {
                    loaderDetail.message = additionalInfo.customLoaderMessage;
                }
                loaderDetail.additionalLoaderData = additionalInfo?.additionalLoaderData;
                loaderDetail.isMutating = isMutating;
                const newRequestId = `req-${++requestIdCounter}`;
                loaderDetail.requestId = newRequestId;
                dispatchLoaderAction({ type: 'add', loaderDetail });
                return newRequestId;
            }
        }
        if (type === 'remove') {
            if (requestId) {
                if (isMutating) {
                    // Mutating loaders are not managed by the LoadingPanel — remove immediately
                    dispatchLoaderAction({ type: 'remove', requestId });
                } else {
                    // Non-mutating loaders transition to 'completed' — panel handles cleanup after debounce
                    dispatchLoaderAction({ type: 'complete', requestId });
                }
            } else {
                dispatchLoaderAction({ type: 'remove', endpoint: endpoint, additionalLoaderData: additionalInfo?.additionalLoaderData });
            }
        }
        return undefined;
    };

    return { state, getApiData, postApiData };
};
